import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import QuoteWidget from '@/components/QuoteWidget';
import Testimonials from '@/components/Testimonials';
import { 
  Shield, 
  BarChart3, 
  Globe2, 
  Lock, 
  ArrowRight, 
  CheckCircle2,
  Building2,
  Users,
  Award,
  Briefcase,
  GraduationCap,
  TrendingUp
} from 'lucide-react';

const HomePage = () => {
  const { t } = useLanguage();

  const clientFeatures = [
    {
      icon: Shield,
      title: "Life Insurance Solutions",
      description: "Compare term, whole, and universal life insurance from A+ rated carriers"
    },
    {
      icon: Building2,
      title: "Estate Planning Support",
      description: "Strategies for wealth transfer and legacy protection"
    },
    {
      icon: BarChart3,
      title: "Premium Financing",
      description: "Preserve capital with sophisticated financing strategies"
    },
    {
      icon: Lock,
      title: "Confidential Advisory",
      description: "Unbiased guidance with complete discretion"
    }
  ];

  const advisorFeatures = [
    {
      icon: Briefcase,
      title: "Build Your Practice",
      description: "Start a career helping families achieve financial security"
    },
    {
      icon: GraduationCap,
      title: "Comprehensive Training",
      description: "Full licensing support and ongoing education"
    },
    {
      icon: TrendingUp,
      title: "Unlimited Income",
      description: "Commission-based earnings with no ceiling"
    },
    {
      icon: Users,
      title: "Leadership Path",
      description: "Grow your team and earn override commissions"
    }
  ];

  const carriers = [
    { name: "MassMutual", rating: "A++" },
    { name: "New York Life", rating: "A++" },
    { name: "Northwestern Mutual", rating: "A++" },
    { name: "Guardian Life", rating: "A++" },
    { name: "Pacific Life", rating: "A+" },
    { name: "Lincoln Financial", rating: "A+" }
  ];

  const stats = [
    { value: "13+", label: "A+ Rated Carriers" },
    { value: "$50B+", label: "Coverage Compared" },
    { value: "1,000+", label: "Licensed Advisors" },
    { value: "50", label: "States Served" }
  ];

  return (
    <div className="bg-[#020617]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1920)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-[#020617]/95 to-[#020617]/80" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <Shield className="w-5 h-5 text-[#C5A059]" />
                <span className="text-[#C5A059] text-sm uppercase tracking-widest">
                  Financial Protection Platform
                </span>
              </div>
              
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
                Financial Protection for Families.
                <span className="block text-[#C5A059] mt-2">Business Opportunity for Advisors.</span>
              </h1>
              
              <p className="text-slate-300 text-lg mb-8 max-w-lg">
                ShopCompareInsure provides modern financial protection solutions including life insurance planning, estate strategies, and advanced premium financing.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/services"
                  data-testid="hero-cta-client"
                  className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all duration-300 group"
                >
                  Explore Insurance
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/for-advisors"
                  data-testid="hero-cta-advisor"
                  className="inline-flex items-center justify-center bg-transparent border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10 px-8 py-4 uppercase tracking-wider text-sm transition-all duration-300"
                >
                  Become an Advisor
                </Link>
              </div>
            </div>

            <div className="hidden lg:block">
              <QuoteWidget />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-slate-800/50 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-serif text-[#C5A059] mb-2">{stat.value}</p>
                <p className="text-slate-500 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two Audiences Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Our Platform Serves Two Audiences
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Whether you're seeking financial protection for your family or looking to build a rewarding career, ShopCompareInsure has solutions for you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* For Clients */}
            <div className="bg-slate-900/50 border border-slate-800 p-8 hover:border-[#C5A059]/30 transition-colors">
              <div className="flex items-center space-x-3 mb-6">
                <Users className="w-8 h-8 text-[#C5A059]" />
                <h3 className="font-serif text-2xl text-white">Families & Individuals</h3>
              </div>
              <p className="text-slate-400 mb-8">
                Access tailored life insurance and wealth protection strategies from top-rated carriers.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {clientFeatures.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div key={idx} className="flex items-start space-x-3">
                      <Icon className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white text-sm font-medium">{feature.title}</p>
                        <p className="text-slate-500 text-xs">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Link
                to="/services"
                className="inline-flex items-center text-[#C5A059] hover:text-[#D4AF37] font-semibold uppercase tracking-wider text-sm group"
              >
                View Insurance Services
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* For Advisors */}
            <div className="bg-slate-900/50 border border-slate-800 p-8 hover:border-[#C5A059]/30 transition-colors">
              <div className="flex items-center space-x-3 mb-6">
                <Briefcase className="w-8 h-8 text-[#C5A059]" />
                <h3 className="font-serif text-2xl text-white">Insurance Advisors</h3>
              </div>
              <p className="text-slate-400 mb-8">
                Join our growing network of licensed agents helping clients secure their financial future.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {advisorFeatures.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div key={idx} className="flex items-start space-x-3">
                      <Icon className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white text-sm font-medium">{feature.title}</p>
                        <p className="text-slate-500 text-xs">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Link
                to="/for-advisors"
                className="inline-flex items-center text-[#C5A059] hover:text-[#D4AF37] font-semibold uppercase tracking-wider text-sm group"
              >
                Explore Career Opportunity
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Quote Widget */}
      <section className="lg:hidden py-16 px-6 bg-[#0F172A]">
        <div className="text-center mb-8">
          <h2 className="font-serif text-2xl text-white mb-2">Get Your Free Quote</h2>
          <p className="text-slate-400 text-sm">Compare rates from top carriers instantly</p>
        </div>
        <QuoteWidget />
      </section>

      {/* Carriers Section */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
                Partnered with America's Most Trusted Carriers
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                We work exclusively with A+ and A++ rated insurance carriers—companies with proven track records of financial stability and claims fulfillment spanning over a century.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Financial strength ratings from A.M. Best, Moody's, and S&P",
                  "Combined assets exceeding $2 trillion",
                  "Over 500 years of combined operating history"
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/carriers"
                className="inline-flex items-center text-[#C5A059] hover:text-[#D4AF37] font-semibold uppercase tracking-wider text-sm group"
              >
                View All Carriers
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {carriers.map((carrier, index) => (
                <div
                  key={index}
                  className="bg-slate-800/50 border border-slate-700 p-6 text-center hover:border-[#C5A059]/30 transition-colors"
                >
                  <p className="font-serif text-white uppercase tracking-wider text-sm mb-2">
                    {carrier.name}
                  </p>
                  <span className="inline-block px-3 py-1 bg-[#C5A059]/10 text-[#C5A059] text-xs font-semibold rounded">
                    {carrier.rating}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              What Our Clients & Advisors Say
            </h2>
            <p className="text-slate-400">
              Real stories from families we've protected and advisors who've built successful careers
            </p>
          </div>
          <Testimonials />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#0F172A] to-[#1E293B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="text-center md:text-left">
              <h3 className="font-serif text-2xl text-white mb-4">Looking for Insurance?</h3>
              <p className="text-slate-400 mb-6">Compare quotes from top-rated carriers in minutes.</p>
              <Link
                to="/compare"
                className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
              >
                Get a Free Quote
              </Link>
            </div>
            <div className="text-center md:text-left">
              <h3 className="font-serif text-2xl text-white mb-4">Want to Build a Career?</h3>
              <p className="text-slate-400 mb-6">Join our network of successful insurance advisors.</p>
              <Link
                to="/become-an-agent"
                className="inline-flex items-center justify-center border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10 px-8 py-4 uppercase tracking-wider text-sm transition-all"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
